// src/pages/CalculatorSavedPage.js
//
// Route: /calculator/result/:token
//
// Fetches the session by public_token and renders CalculatorResult.
// Sidebar adapts to ownership state:
//
//   owned     — "Saved to your account ✓" + hub CTA
//   unclaimed — "Save to my account" (one click, no email)
//   public    — email capture (existing flow)

import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { useProductMode } from '../hooks/useProductMode'
import { fetchCalculatorSessionByToken } from '../lib/sessionByToken'
import CalculatorResult from './CalculatorResult'
import { AutonomoSummaryBlock, hasPersonalFactsFromSession, hasNonZeroSelfEmploymentIncome } from './AutonomoCalculatorPage'

export default function CalculatorSavedPage() {
  const { token }  = useParams()
  const navigate   = useNavigate()
  const { ownershipState, isAuthenticated, user, hubCTA, otherProductDone, authLoading } = useProductMode()
  const fetched    = useRef(false)

  const [uiState,   setUiState]   = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [result,    setResult]    = useState(null)
  const [autonomoResult, setAutonomoResult] = useState(null)
  // BUG FIX: this page never fetched or forwarded these two — meaning
  // filing comparison, the 3rd hero stat, and the owner column never
  // rendered on /calculator/result/:token, only on the live /calculator
  // flow (whose parent page apparently does wire these through). Same
  // "shared component, two parents, one missed the new field" shape
  // flagged in the CTA-testing session recap re: connectedToAdvisor.
  const [filingComparison, setFilingComparison] = useState(null)
  const [autonomoIrpfImpact, setAutonomoIrpfImpact] = useState(null)
  const [session,   setSession]   = useState(null)
  // BUG FIX: this page never read or forwarded published_at, so
  // CalculatorResult always fell back to its default published=false —
  // meaning OwnedSidebar showed "Draft autosaved to your account" even for
  // a genuinely published row (has public_token, published_at set). Since
  // this page is only ever reached via a public_token URL, and
  // publish-session always sets public_token and published_at together,
  // published will in practice always be true here — but deriving it from
  // the actual column rather than hardcoding true keeps this correct if
  // that invariant ever changes (e.g. an unpublish feature later).
  const [published, setPublished] = useState(false)
  const [ownerState, setOwnerState] = useState('public')
  const [partner,    setPartner]    = useState(null)  // { name } — resolved from partner_ref, mirrors TriageResults.js
  const [partnerRef, setPartnerRef] = useState(null)
  // Does this session's tax_subject already have a partner_id set — via a
  // prior advisor-connect submission OR a ?ref= referral claimed earlier
  // (both write the same field, see advisor-connect/index.ts and
  // claim-session/index.ts). Suppresses the advisor CTA in ResultCTA when
  // true. Mirrors the same lookup in TriageResults.js.
  const [alreadyConnectedToAdvisor, setAlreadyConnectedToAdvisor] = useState(false)

  useEffect(() => {
    // Previously ran unconditionally on mount ([] deps), which could fire
    // before AuthContext's initial getSession() resolved — user would
    // still be null, ownershipState({ is_owner, is_claimed }) would incorrectly
    // resolve to 'public' for an already-logged-in user's own session, and
    // the fetched.current guard meant it never got a second chance once
    // real auth state came in. Now waits for authLoading to go false
    // before the one-shot fetch runs at all.
    if (fetched.current || authLoading) return
    fetched.current = true
    init()
  }, [authLoading])

  // Shared claim call — same claim-session edge function AuthCallback.js
  // uses, so a calculator claim behaves identically regardless of whether
  // the user logs in via magic link (AuthCallback.js) or is already
  // logged in and lands here directly (this page). Handles tax_subjects /
  // tax_profiles / tax_year_profiles creation server-side — this page no
  // longer needs (or should have) its own copy of that logic.
  async function claimSession() {
    const { data: { session: authSession } } = await supabase.auth.getSession()
    if (!authSession) return { error: 'Not authenticated' }

    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-session`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${authSession.access_token}`,
        },
        body: JSON.stringify({ product: 'calculator', public_token: token }),
      }
    )
    return res.json().catch(() => ({ error: 'claim-session request failed' }))
  }

  async function init() {
    if (!token) {
      setLoadError('No estimate token found in this URL.')
      setUiState('error')
      return
    }

    // The RPC applies the deleted_at filter: a soft-deleted session's link
    // behaves exactly like an unrecognized token (null data), which the
    // "couldn't find that estimate" error path below already covers.
    const { data, error } = await fetchCalculatorSessionByToken(token)

    if (error || !data) {
      setLoadError("We couldn't find that estimate. The link may be incorrect or the estimate may have been removed.")
      setUiState('error')
      return
    }

    setResult(data.estimate_result)
    setAutonomoResult(data.autonomo_result)
    setFilingComparison(data.filing_comparison)
    setAutonomoIrpfImpact(data.autonomo_irpf_impact)
    setSession(data.session_payload)
    setPublished(!!data.published_at)

    if (data.partner_ref) {
      setPartnerRef(data.partner_ref)
      const { data: partnerRow } = await fetchPartnerNameByRef(data.partner_ref)
      if (partnerRow) setPartner(partnerRow)
    }

    const state = ownershipState({ is_owner: data.is_owner, is_claimed: data.is_claimed })
    setOwnerState(state)

    // Auto-claim removed — silently claiming ANY unclaimed session for
    // whoever happens to be logged in when they view it is unsafe: it
    // never verifies the viewer actually owns this session. Since
    // claim-session's subject resolution reuses the caller's EXISTING
    // tax_subjects row rather than creating a fresh one, this didn't just
    // mislabel sessions — steps 5/6 in claim-session/index.ts blindly
    // overwrite the caller's own tax_profiles/tax_year_profiles with
    // whatever facts were on the session being viewed. Triggered for real
    // via the admin dashboard's deep links (any admin browsing anonymous
    // sessions while logged in silently annexed them, overwriting their
    // own profile in the process). handleManualClaim below (the existing
    // "Save to my account" button) already covers the legitimate case —
    // someone deliberately claiming a session they recognize as their
    // own — without this risk.
    const effectiveSubjectId = data.tax_subject_id

    if (effectiveSubjectId) {
      const { data: subjectRow } = await supabase
        .from('tax_subjects')
        .select('partner_id')
        .eq('id', effectiveSubjectId)
        .maybeSingle()
      setAlreadyConnectedToAdvisor(!!subjectRow?.partner_id)
    }

    setUiState('ready')
  }

  // Called by UnclaimedPanel's manual claim button (fallback if auto-claim failed)
  async function handleManualClaim() {
    if (!user) return
    const claimResult = await claimSession()
    if (!claimResult.error) {
      setOwnerState('owned')
      if (claimResult.tax_subject_id) {
        sessionStorage.setItem('active_subject_id', claimResult.tax_subject_id)
      }
    } else {
      console.error('calculator manual claim error:', claimResult.error)
    }
  }

  if (uiState === 'loading') {
    return (
      <div className="triage-root">
        <div className="triage-col" style={{ paddingTop: '4rem', textAlign: 'center' }}>
          <div className="app-spinner" style={{ margin: '0 auto' }} />
          <p className="triage-hint" style={{ marginTop: '1rem' }}>Loading your estimate…</p>
        </div>
      </div>
    )
  }

  if (uiState === 'error') {
    return (
      <div className="triage-root">
        <div className="triage-col">
          <div className="alert-error" style={{ marginTop: '2rem' }}>{loadError}</div>
          <button
            className="triage-back-link"
            style={{ marginTop: '1rem' }}
            onClick={() => navigate('/calculator', { state: { restoreSession: session } })}
          >
            ← Start a new estimate
          </button>
        </div>
      </div>
    )
  }

  // Determine what to pass as onSave to CalculatorResult's sidebar.
  // owned/unclaimed → null (sidebar shows "Saved ✓" + hub CTA)
  // public → the standard saveSession (triggers email capture)
  const onSave = ownerState === 'public' ? async (email) => {
    // Anonymous save path: delegate to the edge function via the existing
    // useCalculatorForm.saveSession pattern. Here we re-implement the
    // minimal save call needed for the CalculatorSavedPage context.
    // (CalculatorSavedPage doesn't have useCalculatorForm, so we call the
    // edge function directly with the stored session payload + email.)
    // partner_ref is included explicitly since it lives on the row, not
    // inside session_payload, and this call creates a fresh row.
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calculate-irpf`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ ...session, email, partner_ref: partnerRef }),
      }
    )
    const data = await res.json()
    return data.public_token || null
  } : null

  return (
    <CalculatorResult
      result={result}
      session={session}
      filingComparison={filingComparison}
      onBack={() => navigate('/calculator', { state: { restoreSession: session } })}
      onSave={onSave}
      isAuthenticated={isAuthenticated}
      ownerState={ownerState}
      onClaimed={handleManualClaim}
      hubCTA={hubCTA}
      publicToken={token}
      isPartnerLinked={!!partner}
      partnerName={partner?.name}
      otherProductDone={otherProductDone('calculator')}
      userEmail={user?.email || null}
      alreadyConnectedToAdvisor={alreadyConnectedToAdvisor}
      published={published}
      afterHeaderContent={
        // BUG FIX: previously gated only on autonomoResult existing, then
        // on raw item presence. A session can have autonomo_facts + a
        // computed autonomoResult without any non-zero self-employment
        // net income (see hasNonZeroSelfEmploymentIncome's comment in
        // AutonomoCalculatorPage.js) — this rendered a hollow "Your
        // autónomo numbers" card with only a RETA stat and no business
        // breakdown. Now requires non-zero net self-employment income in
        // the computed result before showing the block at all.
        autonomoResult && hasNonZeroSelfEmploymentIncome(result?.classified_items) && (
          <AutonomoSummaryBlock
            autonomoResult={autonomoResult}
            autonomoIrpfImpact={autonomoIrpfImpact}
            result={result}
            hasPersonalFacts={hasPersonalFactsFromSession(session)}
          />
        )
      }
    />
  )
}